/**
 * BoardBuilder — the physical stage: a felt-inlaid hexagram set into a
 * chamfered oak frame with brass beading and star inlays.
 * Materials stay nameable: 毡(felt) / 木(oak) / 黄铜(brass).
 */
import * as THREE from 'three';
import { generateBoardPositions, hexToCartesian } from '@shared/utils/hexMath';
import { CAMP_POSITIONS } from '@shared/constants/camps';
import type { ThemePreset } from '@shared/config/theme';
import {
  createFeltTexture,
  createFeltBump,
  createWoodTexture,
  FELT_WORLD_EXTENT,
} from './textures';

/** Felt & frame radii (world units). Tips of the 121 holes reach 12.0. */
export const R_FELT = 13.2;
export const R_FRAME = 14.8;
export const BOARD_TOP = 0.62;

/**
 * Regular hexagram (six-pointed star) outline with `count` vertices per k*30°.
 * Start at -60° so the six tips align with the hole arms (which sit at world
 * 0/60/120/180/240/300°) — a -90° start leaves half the arms off the felt.
 */
function hexagramShape(rTip: number): THREE.Shape {
  const rInner = rTip / Math.sqrt(3);
  const shape = new THREE.Shape();
  for (let k = 0; k < 12; k++) {
    const angle = (-60 + k * 30) * (Math.PI / 180);
    const r = k % 2 === 0 ? rTip : rInner;
    const x = Math.cos(angle) * r;
    const y = Math.sin(angle) * r;
    if (k === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  }
  shape.closePath();
  return shape;
}

/** Five-point star inlay outline. */
function starShape(rOuter: number): THREE.Shape {
  const shape = new THREE.Shape();
  for (let k = 0; k < 10; k++) {
    const angle = (90 + k * 36) * (Math.PI / 180);
    const r = k % 2 === 0 ? rOuter : rOuter * 0.42;
    const x = Math.cos(angle) * r;
    const y = Math.sin(angle) * r;
    if (k === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  }
  shape.closePath();
  return shape;
}

/** Assign planar UVs so world (x,z) matches the baked felt texture. */
function planarUVs(geo: THREE.BufferGeometry, extent: number): void {
  const pos = geo.getAttribute('position');
  const uvs = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    uvs[i * 2] = 0.5 + x / (2 * extent);
    uvs[i * 2 + 1] = 0.5 - z / (2 * extent);
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
}

export interface BoardHandle {
  group: THREE.Group;
  /** Hole centre world positions keyed by "q,r". */
  holeWorld: Map<string, THREE.Vector3>;
  feltY: number;
  update: (dt: number, elapsed: number) => void;
  /** Micro board-shake on marble landing (±0.3 单位毫米感). */
  shake: (intensity?: number) => void;
  dispose: () => void;
}

export function buildBoard(theme: ThemePreset): BoardHandle {
  const group = new THREE.Group();
  const disposables: { dispose(): void }[] = [];
  const track = <T extends { dispose(): void }>(x: T): T => {
    disposables.push(x);
    return x;
  };

  // ── Materials ──
  const feltTex = track(createFeltTexture(theme));
  const feltBump = track(createFeltBump());
  const wood = createWoodTexture(theme);
  track(wood.map);
  track(wood.bump);

  const feltMat = track(
    new THREE.MeshStandardMaterial({
      map: feltTex,
      bumpMap: feltBump,
      bumpScale: 0.6,
      roughness: 0.96,
      metalness: 0,
    })
  );
  const woodMat = track(
    new THREE.MeshStandardMaterial({
      map: wood.map,
      bumpMap: wood.bump,
      bumpScale: 0.8,
      roughness: 0.62,
      metalness: 0,
    })
  );
  const brassMat = track(
    new THREE.MeshStandardMaterial({
      color: theme.brass,
      metalness: 0.92,
      roughness: 0.34,
      envMapIntensity: 1.25,
    })
  );

  // ── Felt playfield (top face) ──
  const feltGeo = track(new THREE.ShapeGeometry(hexagramShape(R_FELT)));
  feltGeo.rotateX(-Math.PI / 2);
  planarUVs(feltGeo, FELT_WORLD_EXTENT);
  const felt = new THREE.Mesh(feltGeo, feltMat);
  felt.position.y = BOARD_TOP;
  felt.receiveShadow = true;
  group.add(felt);

  // ── Wooden frame ring with chamfer ──
  const frameShape = hexagramShape(R_FRAME);
  const holePath = new THREE.Path();
  // Reuse hexagram outline as the cut-out (slightly inset so felt hides seam).
  const inner = hexagramShape(R_FELT - 0.12);
  holePath.curves = inner.getPoints(12).map((p, i, arr) => {
    const next = arr[(i + 1) % arr.length] ?? p;
    return new THREE.LineCurve(new THREE.Vector2(p.x, p.y), new THREE.Vector2(next.x, next.y));
  });
  frameShape.holes.push(holePath);
  const frameGeo = track(
    new THREE.ExtrudeGeometry(frameShape, {
      depth: 0.56,
      bevelEnabled: true,
      bevelThickness: 0.05,
      bevelSize: 0.05,
      bevelSegments: 2,
    })
  );
  frameGeo.rotateX(-Math.PI / 2);
  const frame = new THREE.Mesh(frameGeo, woodMat);
  frame.position.y = BOARD_TOP - 0.56; // rim lip rises ~0.05 above felt via bevel
  frame.castShadow = true;
  frame.receiveShadow = true;
  group.add(frame);

  // ── Base slab (underside & standing edge) ──
  const baseGeo = track(
    new THREE.ExtrudeGeometry(hexagramShape(R_FRAME), {
      depth: 0.24,
      bevelEnabled: true,
      bevelThickness: 0.04,
      bevelSize: 0.06,
      bevelSegments: 2,
    })
  );
  baseGeo.rotateX(-Math.PI / 2);
  const base = new THREE.Mesh(baseGeo, woodMat);
  base.position.y = BOARD_TOP - 0.56 - 0.24;
  base.castShadow = true;
  base.receiveShadow = true;
  group.add(base);

  // ── Brass beading around the felt ──
  const outline = hexagramShape(R_FELT - 0.12).getPoints(12);
  const path = new THREE.CurvePath<THREE.Vector3>();
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i] ?? new THREE.Vector2();
    const b = outline[(i + 1) % outline.length] ?? new THREE.Vector2();
    path.add(
      new THREE.LineCurve3(new THREE.Vector3(a.x, 0, -a.y), new THREE.Vector3(b.x, 0, -b.y))
    );
  }
  const beadGeo = track(new THREE.TubeGeometry(path, 64, 0.05, 8, true));
  const bead = new THREE.Mesh(beadGeo, brassMat);
  bead.position.y = BOARD_TOP + 0.005;
  group.add(bead);

  // ── Brass star inlays at each camp mouth ──
  const starGeo = track(
    new THREE.ExtrudeGeometry(starShape(0.8), { depth: 0.02, bevelEnabled: false })
  );
  starGeo.rotateX(-Math.PI / 2);
  for (let camp = 0; camp < 6; camp++) {
    const tip = CAMP_POSITIONS[camp]?.[0];
    if (!tip) continue;
    const { x, z } = hexToCartesian(tip);
    const star = new THREE.Mesh(starGeo, brassMat);
    const len = Math.hypot(x, z);
    const r = len - 1.35;
    star.position.set((x / len) * r, BOARD_TOP + 0.012, (z / len) * r);
    star.rotation.y = Math.atan2(-x, -z);
    star.scale.setScalar(0.9);
    group.add(star);
  }

  // ── Hole registry (picking + marble placement) ──
  const holeWorld = new Map<string, THREE.Vector3>();
  for (const pos of generateBoardPositions()) {
    const { x, z } = hexToCartesian(pos);
    holeWorld.set(`${pos.q},${pos.r}`, new THREE.Vector3(x, BOARD_TOP, z));
  }

  // ── Landing micro-shake ──
  let shakeAmp = 0;
  const shake = (intensity = 1): void => {
    shakeAmp = Math.max(shakeAmp, 0.03 * intensity);
  };
  const update = (dt: number, elapsed: number): void => {
    if (shakeAmp > 0.0004) {
      group.position.y = Math.sin(elapsed * 90) * shakeAmp;
      shakeAmp *= Math.pow(0.001, dt); // fast decay
    } else {
      group.position.y = 0;
      shakeAmp = 0;
    }
  };

  const dispose = (): void => {
    for (const d of disposables) d.dispose();
    group.clear();
  };

  return { group, holeWorld, feltY: BOARD_TOP, update, shake, dispose };
}
